import { toast } from "../../../../layout/app-toast/utils/toastManager.js";
import { apiFetch, isErrorResult } from "../../../../../app/apiClient.js";
import { selectDialogDropdowns } from "../dropdowns/dropdownManager.js";
import { getContent } from "../../../app-table/tableManager/tableManager.js";
import { openDialog } from "../core/dialogManager.js";
import { editCustomer, editCustomerDashboardModules, editCustomerPermittedLanguages, editCustomerApplications, editCustomerServices } from "../../../app-table/tableManager/customerManager.js";
import { currentContext } from "../core/dialogContext.js";
import { withClose, chainSetup } from "../core/dialogHandlerUtils.js";
import { setupLanguageDropdown, setupDashboardModulesDropdown, setupPermittedLanguagesDropdown } from "../dropdowns/dropdownInits.js";
import { renderItemCards } from "../core/dialogBuilder.js";
import { excludedCatalogIds } from "./itemManagerHandlers.js";

export function buildEditCustomerHandlers(data) {
    currentContext.id = data?.id;
    const handlers = {};
    return Object.assign(handlers, {
        onSave: withClose(() => editCustomer(currentContext.id)),
        initLanguageDropdown: (element) => chainSetup(handlers, setupLanguageDropdown(element, data)),
        setup: () => selectDialogDropdowns(data)
    });
}

export function buildEditCustomerDashboardModulesHandlers(data) {
    currentContext.id = data?.id;
    const handlers = {};
    return Object.assign(handlers, {
        onSave: withClose(async () => {
            const content = getContent();
            const selected = content?.querySelector('#inputCustomerDashboardModules')?.getValue?.() ?? [];
            const result = await editCustomerDashboardModules(currentContext.id, selected.map(Number));
            if (result === true) return true;
            document.dispatchEvent(new CustomEvent('customer-data-changed', {
                detail: { action: 'dashboardModules-updated', customer: { id: currentContext.id }, dashboardModules: result },
                bubbles: true, composed: true,
            }));
        }),
        initDashboardModulesDropdown: (element) => chainSetup(handlers, setupDashboardModulesDropdown(element, data)),
        setup: () => selectDialogDropdowns(data),
    });
}

export function buildEditCustomerPermittedLanguagesHandlers(data) {
    currentContext.id = data?.id;
    const handlers = {};
    return Object.assign(handlers, {
        onSave: withClose(async () => {
            const content = getContent();
            const selected = content?.querySelector('#inputCustomerPermittedLanguages')?.getValue?.() ?? [];
            const result = await editCustomerPermittedLanguages(currentContext.id, selected.map(Number));
            if (result === true) return true;
            document.dispatchEvent(new CustomEvent('customer-data-changed', {
                detail: { action: 'permittedLanguages-updated', customer: { id: currentContext.id }, permittedLanguages: result },
                bubbles: true, composed: true,
            }));
        }),
        initPermittedLanguagesDropdown: (element) => chainSetup(handlers, setupPermittedLanguagesDropdown(element, data)),
        setup: () => selectDialogDropdowns(data),
    });
}

export function buildEditCustomerApplicationsHandlers(data) {
    currentContext.id = data?.id;
    currentContext.existingApps = new Map();
    currentContext.toAdd    = new Map();
    currentContext.toRemove = new Set();
    currentContext.draft    = null;
    let listEl, rowRef;

    const renderApps = () => {
        const existing = [...currentContext.existingApps.values()].filter(a => !currentContext.toRemove.has(String(a.id)));
        const items = [...existing, ...currentContext.toAdd.values(), ...(currentContext.draft ? [currentContext.draft] : [])];
        renderItemCards(listEl, items, rowRef, currentContext.draft);
    };

    const handlers = {};
    return Object.assign(handlers, {
        onSave: withClose(async () => {
            const remaining  = [...currentContext.existingApps.values()].filter(a => !currentContext.toRemove.has(String(a.id)));
            const added      = [...currentContext.toAdd.values()];
            const applications = [...remaining, ...added].map(application => ({ applicationId: application.id, url: application.url ?? '' }));
            const result = await editCustomerApplications(currentContext.id, applications);
            if (result === true) return true;
            const enriched = (result?.customerApplicaton ?? []).map(app => ({ id: app.applicationId, name: app.name, url: app.url }));
            document.dispatchEvent(new CustomEvent('customer-data-changed', {
                detail: { action: 'applications-updated', customer: { id: currentContext.id }, applications: enriched, projects: result?.customerProject ?? [] },
                bubbles: true, composed: true,
            }));
        }),
        onAddToggle: () => {
            if (currentContext.draft) return;
            currentContext.draft = {};
            renderApps();
        },
        getFieldOptions: () => {
            const excluded = excludedCatalogIds(currentContext.existingApps, currentContext.toRemove, currentContext.toAdd);
            return (currentContext.allApps ?? []).filter(app => !excluded.has(String(app.id)));
        },
        onConfirm: (item, values) => {
            const selectedId = String(values.id ?? '');
            if (!selectedId) return false;
            const url = String(values.url ?? '').trim();
            if (!url) {
                toast("Bitte eine URL eingeben.", "error");
                return false;
            }
            const application = currentContext.allApps.find(application => String(application.id) === selectedId);
            if (!application) return false;
            currentContext.toAdd.set(selectedId, { id: application.id, name: application.name, url });
            currentContext.draft = null;
            renderApps();
            return true;
        },
        onRemove: (item) => {
            if (item === currentContext.draft) {
                currentContext.draft = null;
                renderApps();
                return;
            }
            const id = String(item.id);
            if (currentContext.toAdd.has(id)) currentContext.toAdd.delete(id);
            else currentContext.toRemove.add(id);
            renderApps();
        },
        onMount: async (container, row) => {
            listEl = container.querySelector('.appManagerList');
            rowRef = row;
            const [allApps, existingApps] = await Promise.all([
                apiFetch(`Applications`),
                apiFetch(`CustomerApplication/${currentContext.id}`),
            ]);
            currentContext.allApps = (allApps ?? []).map(application => ({ id: application.id, name: application.name }));
            currentContext.existingApps = new Map(
                (existingApps ?? []).map(application => [String(application.applicationId), { id: application.applicationId, name: application.name, url: application.url }])
            );
            renderApps();
        },
    });
}

export function buildEditCustomerServicesHandlers(data) {
    const sameSession = currentContext.servicesSessionId != null && currentContext.servicesSessionId === data?.id;
    currentContext.id = data?.id;
    currentContext.servicesSessionId = data?.id;
    if (!sameSession) {
        currentContext.existingServices = new Map();
        currentContext.toAdd    = new Map();
        currentContext.toRemove = new Set();
        currentContext.draft    = null;
    }
    let listEl, rowRef;

    const renderServices = () => {
        const existing = [...currentContext.existingServices.values()].filter(s => !currentContext.toRemove.has(String(s.id)));
        const items = [...existing, ...currentContext.toAdd.values(), ...(currentContext.draft ? [currentContext.draft] : [])];
        renderItemCards(listEl, items, rowRef, currentContext.draft);
    };

    const loadServices = async () => {
        const [allServices, bookedServices] = await Promise.all([
            apiFetch(`Services`),
            apiFetch(`Services?customerId=${currentContext.id}`),
        ]);
        currentContext.allServices = allServices ?? [];
        currentContext.existingServices = new Map(
            (bookedServices ?? []).map(service => [String(service.id), { id: service.id, name: service.name, isTranslationActive: service.isTranslationActive, hasConfiguration: service.hasConfiguration }])
        );
    };

    const handlers = {};
    return Object.assign(handlers, {
        onSave: withClose(async () => {
            const remaining = [...currentContext.existingServices.values()].filter(s => !currentContext.toRemove.has(String(s.id)));
            const added     = [...currentContext.toAdd.values()];
            const allItems  = [...remaining, ...added];
            const services  = allItems.map(service => ({ id: service.id, isTranslationActive: !!service.isTranslationActive }));
            const result = await editCustomerServices(currentContext.id, services);
            if (result === true) return true;

            const configTargets = allItems.filter(service => service.configuration !== undefined);
            for (const service of configTargets) {
                const configResult = await apiFetch(`Services/${currentContext.id}/${service.id}/configuration`, 'PATCH', service.configuration);
                if (isErrorResult(configResult)) return true;
            }

            const enriched = (result ?? []).map(service => ({
                bookedServiceId: service.id,
                bookedServiceName: service.name,
                description: service.description,
                url: service.url,
                isTranslationActive: service.isTranslationActive,
                hasConfiguration: service.hasConfiguration,
            }));
            document.dispatchEvent(new CustomEvent('customer-data-changed', {
                detail: { action: 'services-updated', customer: { id: currentContext.id }, services: enriched },
                bubbles: true, composed: true,
            }));

            currentContext.toAdd.clear();
            currentContext.toRemove.clear();
            currentContext.draft = null;
            currentContext.servicesSessionId = null;
        }),
        onAddToggle: () => {
            if (currentContext.draft) return;
            currentContext.draft = {};
            renderServices();
        },
        getFieldOptions: () => {
            const excluded = excludedCatalogIds(currentContext.existingServices, currentContext.toRemove, currentContext.toAdd);
            return (currentContext.allServices ?? []).filter(service => !excluded.has(String(service.id)));
        },
        onConfirm: (item, values) => {
            const selectedId = String(values.id ?? '');
            if (!selectedId) return false;
            const service = currentContext.allServices.find(service => String(service.id) === selectedId);
            if (!service) return false;
            currentContext.toAdd.set(selectedId, { id: service.id, name: service.name, isTranslationActive: false });
            currentContext.draft = null;
            renderServices();
            return true;
        },
        onRemove: (item) => {
            if (item === currentContext.draft) {
                currentContext.draft = null;
                renderServices();
                return;
            }
            const id = String(item.id);
            if (currentContext.toAdd.has(id)) currentContext.toAdd.delete(id);
            else currentContext.toRemove.add(id);
            renderServices();
        },
        onToggleTranslation: (item) => {
            item.isTranslationActive = !item.isTranslationActive;
        },
        onServiceConfig: (item) => {
            const isBooked = currentContext.existingServices.has(String(item.id)) && !currentContext.toRemove.has(String(item.id));
            openDialog('editServiceConfiguration', {
                customerId: currentContext.id,
                serviceId: item.id,
                serviceName: item.name,
                isBooked,
                configuration: item.configuration,
            });
        },
        onMount: async (container, row) => {
            listEl = container.querySelector('.appManagerList');
            rowRef = row;
            if (!sameSession) await loadServices();
            renderServices();
        },
    });
}

const SERVICE_CONFIG_CONTEXT_KEY = 'dlg_editServiceConfiguration_context';

export function buildEditServiceConfigurationHandlers(data) {
    if (!data?.customerId || !data?.serviceId) {
        try {
            data = JSON.parse(sessionStorage.getItem(SERVICE_CONFIG_CONTEXT_KEY));
        } catch {
            data = null;
        }
        if (!data?.customerId || !data?.serviceId) return {};
    } else {
        sessionStorage.setItem(SERVICE_CONFIG_CONTEXT_KEY, JSON.stringify(data));
    }

    currentContext.id = data.customerId;
    const handlers = {};
    return Object.assign(handlers, {
        serviceName: data.serviceName,
        onClose: () => sessionStorage.removeItem(SERVICE_CONFIG_CONTEXT_KEY),
        onBack: () => openDialog('editCustomerServices', { id: data.customerId }),
        setupPromise: import('../../../../shared/app-json-editor/app-json-editor.js'),
        initJsonEditor: async (element) => {
            await customElements.whenDefined('app-json-editor');

            if (data.configuration !== undefined && data.configuration !== null) {
                element.setValue(JSON.stringify(data.configuration, null, 2));
                return;
            }
            if (!data.isBooked) {
                element.setValue('{}');
                return;
            }
            try {
                const configuration = await apiFetch(`Services/${data.customerId}/${data.serviceId}/configuration`);
                const hasConfiguration = configuration !== null && typeof configuration === 'object';
                element.setValue(hasConfiguration ? JSON.stringify(configuration, null, 2) : '{}');
            } catch (error) {
                console.error("Konfiguration konnte nicht geladen werden.", error);
                element.setValue?.('{}');
            }
        },
        onSave: () => {
            const editor = getContent()?.querySelector('#inputServiceConfiguration');
            const raw = editor?.getValue?.() ?? '';

            let parsed;
            try {
                parsed = JSON.parse(raw);
            } catch {
                toast("Ungültiges JSON.", "error");
                return;
            }

            const target = currentContext.toAdd?.get(String(data.serviceId)) ?? currentContext.existingServices?.get(String(data.serviceId));
            if (target) {
                target.configuration = parsed;
                target.hasConfiguration = true;
            }

            toast(data.isBooked ? "Konfiguration wird beim Speichern übernommen." : "Konfiguration hinzugefügt, bitte anschließend speichern.", "success");
            openDialog('editCustomerServices', { id: data.customerId });
        },
    });
}